<script lang="ts" setup>
import { computed } from 'vue'
import { translate } from '@/i18n/i18n.ts'
import type { ProductOfferDto } from '@/ts/types/insurance.dto.ts'
import type { ProductSpecificationResponse } from '@/libs/api/insurance/insurance.types'
import type { RedirectBehavior } from '@/ts/types/redirect.types'
import type { ModalConfig } from '@/ts/types/modal.types'
import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import { useUiStore } from '@/stores/ui.store.ts'

import Collapsible from '@/components/atom/collapsable/Collapsible.vue'
import PriceDisplayLabel from '@/components/atom/price/PriceDisplayLabel.vue'
import DocumentListItem from '@/components/atom/list-item/document/DocumentListItem.vue'
import RedirectButton from '@/components/atom/redirect/RedirectButton.vue'
import ListItemWithIcon from '@/components/atom/list-item/ListItemWithIcon.vue'

type Props = {
  offer: ProductOfferDto & { deviceClassId?: string }
  specs: ProductSpecificationResponse
  targetUrl: string
  redirectBehavior: RedirectBehavior
}

type ProductOfferCardEmits = {
  openModal: [modal: ModalConfig]
  closeModal: []
}

const props = withDefaults(defineProps<Props>(), {
  redirectBehavior: 'target_blank'
})

const uiStore = useUiStore()
const connectStore = useConnectStore()

const advantages = computed(() => {
  return props.offer.content?.advantages
})

const disadvantages = computed(() => {
  return props.offer.content?.disadvantages
})

const documents = computed(() => {
  return props.offer.documents
})

const paymentInterval = computed(() => {
  const intervalStringMapping: Record<number, string> = {
    0: 'product.payment_interval_single',
    1: 'product.payment_interval_monthly',
    3: 'product.payment_interval_quarterly',
    6: 'product.payment_interval_semi_yearly',
    12: 'product.payment_interval_annually'
  }
  return intervalStringMapping[props.specs.price_interval as number] ?? ''
})

const showPriceOverview = computed(() => {
  return props.offer.deviceClassId !== connectStore.batteryDeviceId
})

const handleOpenPriceInfoModal = () => {
  uiStore.setModal(
    'PriceInfoModal',
    {
      header: {
        title: translate('modal_content.price_info.headline', props.offer?.title || '')
      },
      footer: {
        closeButton: {
          label: translate('dialog.close_button_text'),
          visible: true
        }
      }
    },
    {
      priceGroups: props.offer?.price_groups?.new || [],
      name: props.offer?.title || ''
    }
  )
}

const emit = defineEmits<ProductOfferCardEmits>()
</script>

<template>
  <div class="product-offer-card">
    <div class="product-offer-card__container">
      <div class="product-offer-card__wrapper">
        <h4 class="product-offer-card__headline">{{ offer.title }}</h4>
        <div class="product-offer-card__content">
          <ul>
            <li v-for="(advantage, index) in advantages" :key="`advantage-${index}`">
              <ListItemWithIcon :text="advantage.body" icon="lnx-check_circle" />
            </li>
            <template v-if="disadvantages">
              <li v-for="(disadvantage, index) in disadvantages" :key="`disadvantage-${index}`">
                <ListItemWithIcon :text="disadvantage.body" icon="lnx-close" />
              </li>
            </template>
          </ul>
        </div>
      </div>
      <div class="product-offer-card__wrapper product-offer-card__wrapper--price">
        <PriceDisplayLabel
          class="product-offer-card__price"
          suffix-position="end"
          :is-loading="false"
          :price="specs.price"
          :suffix-label="translate(paymentInterval)"
          :show-info-icon="showPriceOverview"
          @openPriceTableModal="handleOpenPriceInfoModal"
        />
        <RedirectButton
          ref="redirectButton"
          :label="translate('product.select_tariff')"
          :target-url="targetUrl"
          :targetBehavior="props.redirectBehavior"
          @open-modal="emit('openModal', $event)"
          @close-modal="emit('closeModal')"
          class="product-offer-card__redirect-button"
        />
      </div>
    </div>
    <Collapsible
      :label="translate('product.open_tariff_details')"
      :label-expanded="translate('product.close_tariff_details')"
    >
      <div class="product-offer-card__container product-offer-card__details">
        <div class="product-offer-card__content">
          <ul class="product-offer-card__document-list">
            <li v-for="(doc, index) in documents" :key="`document-${index}`">
              <DocumentListItem :document="doc" />
            </li>
          </ul>
        </div>
      </div>
    </Collapsible>
  </div>
</template>

<style lang="scss" scoped>
@import 'product-offer-card';
</style>
